"use client";

import { SignUp, useAuth, useClerk, useSignUp, useUser } from "@clerk/nextjs";
import { useEffect, useRef } from "react";
import { Loader2, Mail, UserPlus } from "lucide-react";
import {
  clerkAuthAppearance,
  clerkSignUpLocalization,
} from "~/lib/clerk-auth-appearance";
import { cn } from "~/lib/utils";

type HireOnboardingAuthPanelProps = {
  phase: "signup" | "verify-email";
  signInUrl: string;
  /** After email-link verification, Clerk must return here to finish org provisioning. */
  hireCompletionRedirectUrl: string;
  initialEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
  /** Product name in copy (SiteGlue vs Inbound GPT). */
  productLabel?: string;
  className?: string;
  onVerified: () => void;
};

const hireSignUpAppearance = {
  ...clerkAuthAppearance,
  elements: {
    ...clerkAuthAppearance.elements,
    rootBox: "w-full max-w-full",
    card: "shadow-none border-0 bg-transparent p-0 w-full max-w-full",
    footer: "pt-2 !flex justify-center",
    footerAction: "!flex justify-center text-sm",
    footerActionText: "text-muted-foreground",
    footerActionLink: "text-primary font-medium hover:text-primary/80",
  },
};

/** Inline Clerk Sign Up (no Dialog) for SiteGlue hire-builder gated tabs. */
export function HireOnboardingAuthPanel({
  phase,
  signInUrl,
  hireCompletionRedirectUrl,
  initialEmail,
  initialFirstName,
  initialLastName,
  productLabel = "SiteGlue",
  className,
  onVerified,
}: HireOnboardingAuthPanelProps) {
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const { setActive: setClerkActive } = useClerk();
  const verifiedOnce = useRef(false);
  const activatingSession = useRef(false);

  const primaryEmail = user?.primaryEmailAddress;
  const needsVerification =
    isSignedIn &&
    primaryEmail &&
    primaryEmail.verification.status !== "verified";

  const awaitingSignUpEmailCode =
    signUpLoaded &&
    !!signUp &&
    signUp.status === "missing_requirements" &&
    (signUp.unverifiedFields.includes("email_address") ||
      signUp.verifications.emailAddress?.status === "unverified");

  const awaitingLinkVerification =
    (phase === "verify-email" || (isSignedIn && needsVerification)) &&
    !awaitingSignUpEmailCode;

  const verifyEmail =
    primaryEmail?.emailAddress ??
    signUp?.emailAddress ??
    initialEmail ??
    "your inbox";

  useEffect(() => {
    if (!signUpLoaded || !signUp || activatingSession.current) return;
    if (signUp.status !== "complete" || !signUp.createdSessionId) return;

    activatingSession.current = true;
    void setClerkActive({ session: signUp.createdSessionId }).catch(() => {
      activatingSession.current = false;
    });
  }, [signUpLoaded, signUp, setClerkActive]);

  useEffect(() => {
    if (!authLoaded || !userLoaded || verifiedOnce.current) return;
    if (!isSignedIn || needsVerification) return;

    verifiedOnce.current = true;
    onVerified();
  }, [authLoaded, userLoaded, isSignedIn, needsVerification, onVerified]);

  const initialValues =
    initialEmail || initialFirstName || initialLastName
      ? {
          ...(initialEmail ? { emailAddress: initialEmail } : {}),
          ...(initialFirstName ? { firstName: initialFirstName } : {}),
          ...(initialLastName ? { lastName: initialLastName } : {}),
        }
      : undefined;

  return (
    <div
      className={cn(
        "flex h-full min-h-[520px] flex-col overflow-y-auto bg-white",
        className,
      )}
    >
      <div className="mx-auto w-full max-w-md px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#4f81bd]/10 text-[#4f81bd]">
            <UserPlus className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900">
              {awaitingSignUpEmailCode || awaitingLinkVerification
                ? "Verify your email"
                : "Create your account"}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {awaitingSignUpEmailCode ? (
                <>
                  Enter the verification code we sent to{" "}
                  <span className="font-medium text-slate-900">{verifyEmail}</span>.
                </>
              ) : awaitingLinkVerification ? (
                <>
                  We sent a verification link to{" "}
                  <span className="font-medium text-slate-900">{verifyEmail}</span>. Click
                  the link, then return here.
                </>
              ) : (
                <>
                  Create your free {productLabel} account to unlock Profile, Preview, Test,
                  and Onboard for your AI Employee.
                </>
              )}
            </p>
          </div>
        </div>

        {awaitingLinkVerification ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-slate-200 bg-slate-50/80 px-6 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Mail className="h-7 w-7" />
            </div>
            <p className="text-sm text-muted-foreground">Waiting for verification…</p>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-4 sm:px-4">
            <SignUp
              routing="hash"
              signInUrl={signInUrl}
              fallbackRedirectUrl={hireCompletionRedirectUrl}
              forceRedirectUrl={hireCompletionRedirectUrl}
              initialValues={initialValues}
              localization={clerkSignUpLocalization}
              appearance={hireSignUpAppearance}
            />
          </div>
        )}
      </div>
    </div>
  );
}
